import { useMemo, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useOutletContext } from "react-router-dom";
import { Organization } from "@/types/organizations";
import { useAccessControl, withAccessControl } from "@/features/access-control";
import { OrganizationContext } from "@/features/organizations/components/OrganizationLayout";
import PageHeader from "@/components/PageHeader";
import useSnackbar from "@/hooks/useSnackbar";
import ColorSetting from "./components/ColorSetting";
import Section from "./components/Section";
import FileSetting from "./components/FileSetting";
import SwitchSetting from "./components/SwitchSetting";
import { useUpdateOrganizationSettingsMutation } from "@/features/organizations/api/organizationsApi";
import { useTerminology } from "@/hooks";
import LoadingButton from "@mui/lab/LoadingButton";
import { get, pick, cloneDeep } from "lodash";
import SelectSetting from "./components/SelectSetting";
import { HugeiconsIcon } from "@hugeicons/react";
import { FolderLibraryIcon, Home06Icon, PaintBrush02Icon, ServiceIcon, ShoppingCart02Icon, Money03Icon, Delete02Icon, Pan03Icon, Scooter02Icon, AddIcon, Cashier02Icon, ZapIcon } from "@hugeicons/core-free-icons";
import { bankOptions, creditCardOptions, currencyOptions } from "@/constants/paymentMethods";
import FlowScheduleSetting from "./components/FlowScheduleSetting";
import SettingsContainer from "./components/SettingsContainer";
import DeliverySettings from "./components/DeliverySettings";
import OperationTimingSettings from "./components/OperationTimingSettings";
import TutorialsSubscriber from "@/components/TutorialsSubscriber";
import { ProductIds } from "@/constants/productIds";
import LoyaltySettingsSection from "./components/LoyaltySettingsSection";
import { Ticket01Icon } from "@hugeicons/core-free-icons";
import AppConfig from "@/components/AppConfig";
import { Configs } from "@/constants/configs";
import { useUpdateOrganizationDriversBatchMutation } from "@/api/driversApi";
import DriversForm from "./components/DriversForm";

const EMPTY_BANK = { bankName: "", currency: "", accountNumber: "" };

const SETTINGS_FIELDS = ["settings", "logo", "isotype", "header", "primaryColor", "secondaryColor"];

export type OrganizationSettingsForm = Pick<Organization, "settings" | "logo" | "isotype" | "header" | "primaryColor" | "secondaryColor">;

const SettingsSchema = Yup.object().shape({
  settings: Yup.object().shape({
    drivers: Yup.array().of(Yup.object().shape({
      name: Yup.string().required("Campo requerido"),
      phone: Yup.string()
        .length(8, 'El teléfono debe tener 8 dígitos')
        .required('Campo requerido'),
    })),
    services: Yup.object().shape({
      delivery: Yup.object().shape({
        isEnabled: Yup.boolean(),
        maxDistanceKm: Yup.number().nullable().min(1, "La distancia máxima debe ser mayor a 0"),
        type: Yup.string().oneOf(["fixed", "manual", "auto"]),
        fee: Yup.object()
          .shape({
            value: Yup.number().min(0),
            minValue: Yup.number().min(0),
            baseRate: Yup.number().min(0),
            valuePerDistance: Yup.number().min(0),
          })
          .when("type", {
            is: "auto",
            then: (schema) =>
              schema.shape({
                baseRate: Yup.number().min(0).required("Campo requerido"),
                valuePerDistance: Yup.number().min(0).required("Campo requerido"),
              }),
          })
          .when("type", {
            is: "fixed",
            then: (schema) =>
              schema.shape({
                value: Yup.number().min(0).required("Campo requerido"),
              }),
          }),
        schedule: Yup.object(),
      }),
      pickUp: Yup.object().shape({
        isEnabled: Yup.boolean(),
      }),
      dineIn: Yup.object().shape({
        isEnabled: Yup.boolean(),
      }),
      orderManagement: Yup.string(),
    }),
    includeTax: Yup.boolean(),
    paymentMethods: Yup.object().shape({
      cash: Yup.boolean(),
      creditCard: Yup.array()
        .of(Yup.string().oneOf(["visa", "mastercard", "discover", "dinners-club", "american-express"]))
        .nullable(),
      bankTransfer: Yup.array()
        .of(
          Yup.object().shape({
            accountNumber: Yup.string(),
            bankName: Yup.string(),
            currency: Yup.string(),
          }),
        )
        .nullable(),
      stripeEnabled: Yup.boolean().required(),
    }),
    operationTiming: Yup.object().shape({
      review: Yup.number().min(0).max(60).nullable(),
      preparation: Yup.number().min(0).max(60).nullable(),
      shipping: Yup.number().min(0).max(60).nullable(),
    }),
    loyaltyProgram: Yup.object().shape({
      isEnabled: Yup.boolean(),
      programName: Yup.string().when("isEnabled", {
        is: true,
        then: (schema) => schema.required("Campo requerido"),
      }),
      pointsPerAmount: Yup.number()
        .min(1, "Debe ser al menos 1")
        .when("isEnabled", {
          is: true,
          then: (schema) => schema.required("Campo requerido"),
        }),
    }),
  })
});

const Settings = () => {
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { currentOrganization } = useOutletContext<OrganizationContext>();
  const { t } = useTerminology();
  const [updateSettings, { isLoading }] = useUpdateOrganizationSettingsMutation();
  const [updateOrganizationDriversBatch] = useUpdateOrganizationDriversBatchMutation();
  const manualCreditCardsCacheRef = useRef<string[]>([]);

  const handleDeleteOrg = () => {
    // TODO: Implement delete organization logic
  };

  const handleSubmit = async (values: OrganizationSettingsForm) => {
    try {
      const result: any = await updateOrganizationDriversBatch({
        organizationId: currentOrganization.id,
        driversFromForm: values.settings.drivers || [],
      });

      if (result.error) {
        openSnackbar(`Error: ${result.error.statusText}`, "error");
      } else {
        openSnackbar("Drivers actualizados correctamente", "success");
      }

      const settingsPayload = cloneDeep(values);
      if (settingsPayload.settings) {
        (settingsPayload.settings as any).drivers = result.data?.driverIds || [];

        if (settingsPayload.settings.paymentMethods) {
          const isStripeActive = Boolean(settingsPayload.settings.paymentMethods.stripeEnabled);
          settingsPayload.settings.paymentMethods.stripeEnabled = isStripeActive;
          if (isStripeActive) {
            settingsPayload.settings.paymentMethods.creditCard = null;
          } else {
            settingsPayload.settings.paymentMethods.creditCard = Array.isArray(
              settingsPayload.settings.paymentMethods.creditCard
            )
              ? settingsPayload.settings.paymentMethods.creditCard
              : [];
          }
        }
      }

      const response = await updateSettings({
        organizationId: currentOrganization?.id,
        payload: settingsPayload,
      });

      if (response?.error) {
        openSnackbar("Error al guardar ajustes generales", "error");
      } else {
        openSnackbar("Configuraciones actualizadas con éxito", "success");
      }
    } catch (err) {
      openSnackbar("Error crítico en el guardado", "error");
    }
  };

  const initialState = useMemo(() => {
    let draft = {
      settings: {
        services: {
          delivery: {
            isEnabled: false,
            type: "manual",
            maxDistanceKm: null,
            schedule: {} as any,
          },
          pickUp: {
            isEnabled: false,
            type: "manual",
            schedule: {} as any,
          },
          dineIn: {
            isEnabled: true,
            type: "manual",
            schedule: {} as any,
          },
          orderManagement: "bitelink",
        },
        includeTax: false,
        paymentMethods: {
          cash: true,
          creditCard: [],
          bankTransfer: [],
          stripeEnabled: false,
        },
        operationTiming: {
          review: 5,
          preparation: 20,
          shipping: 30,
        },
        loyaltyProgram: {
          isEnabled: false,
          programName: "",
          pointsPerAmount: 100,
        },
        drivers: [],
      },
    };

    if (currentOrganization) {
      // Create a deep copy to safely mutate nested read-only objects from the RTK query cache
      draft = cloneDeep(pick(currentOrganization, SETTINGS_FIELDS)) as any;

      if (!draft.settings) {
        draft.settings = {
          services: {
            delivery: { isEnabled: false, type: "manual", schedule: {} as any },
            pickUp: { isEnabled: false, type: "manual", schedule: {} as any },
            dineIn: { isEnabled: true, type: "manual", schedule: {} as any },
          },
          orderManagement: "system",
          includeTax: false,
          paymentMethods: { cash: true, creditCard: [], bankTransfer: [] },
          operationTiming: { review: 5, preparation: 20, shipping: 30 },
        } as any;
      }

      if (!draft.settings.loyaltyProgram) {
        draft.settings.loyaltyProgram = {
          isEnabled: false,
          programName: "",
          pointsPerAmount: 100,
        };
      }

      const pm = draft.settings?.paymentMethods;
      if (pm) {
        if (typeof pm.creditCard === "boolean") (pm as any).creditCard = undefined;
        if (typeof pm.bankTransfer === "boolean") (pm as any).bankTransfer = undefined;

        if (Array.isArray(pm.creditCard) && pm.creditCard.length > 0) {
          manualCreditCardsCacheRef.current = [...pm.creditCard];
        }

        const isStripeActive = Boolean(pm.stripeEnabled);
        pm.stripeEnabled = isStripeActive;
        if (isStripeActive) {
          pm.creditCard = null;
        } else {
          pm.creditCard = Array.isArray(pm.creditCard) ? pm.creditCard : [];
        }
      } else if (draft.settings) {
        draft.settings.paymentMethods = {
          cash: true,
          creditCard: [],
          bankTransfer: [],
          stripeEnabled: false,
        };
      }

      if (Array.isArray(draft.settings?.drivers)) {
        draft.settings.drivers = draft.settings.drivers.filter(
          (d: any) => typeof d === "object" && d !== null
        );
      }
    }

    return draft as unknown as OrganizationSettingsForm;
  }, [currentOrganization]);

  if (!currentOrganization) return null;


  return (
    <Formik enableReinitialize initialValues={initialState} validationSchema={SettingsSchema} onSubmit={handleSubmit}>
      {({ values, setFieldValue, handleChange }) => {
        const isStripeEnabled = Boolean(values.settings?.paymentMethods?.stripeEnabled);
        const bankTransferEnabled = !!values.settings?.paymentMethods?.bankTransfer;

        const handleSelectPaymentModality = (mode: "auto" | "manual") => {
          if (mode === "auto") {
            if (isStripeEnabled) return;
            const currentCards = values.settings?.paymentMethods?.creditCard;
            if (Array.isArray(currentCards)) {
              manualCreditCardsCacheRef.current = [...currentCards];
            }
            setFieldValue("settings.paymentMethods.stripeEnabled", true);
            setFieldValue("settings.paymentMethods.creditCard", null);
          } else {
            if (!isStripeEnabled && Array.isArray(values.settings?.paymentMethods?.creditCard)) return;
            setFieldValue("settings.paymentMethods.stripeEnabled", false);
            setFieldValue(
              "settings.paymentMethods.creditCard",
              manualCreditCardsCacheRef.current.length ? [...manualCreditCardsCacheRef.current] : []
            );
          }
        };

        return (
          <Form>
            <Stack spacing={4}>
              <TutorialsSubscriber productIds={[ProductIds.DELIVERY]} />
              <PageHeader
                title="Configuraciones"
                action={
                  hasAccess("update.organization") ? (
                    <Button size="large" color="success" type="submit">
                      Guardar
                    </Button>
                  ) : undefined
                }
              />
              <Stack alignItems="center">
                <Box sx={{ maxWidth: 800, width: "100%" }} mb={3}>
                  <Stack spacing={4}>
                    <Section title="Cocina" icon={<HugeiconsIcon icon={Pan03Icon} />}>
                      <OperationTimingSettings />
                    </Section>

                    <Section title="Repartidores" icon={<HugeiconsIcon icon={Scooter02Icon} />}>
                      <Paper sx={{ p: 2, gap: 2 }}>
                        <Stack spacing={2}>

                          <DriversForm />

                        </Stack>
                      </Paper>
                    </Section>

                    <Section title="Servicios" icon={<HugeiconsIcon icon={ServiceIcon} />}>
                      <Stack spacing={2}>
                        <SwitchSetting
                          title={t("establishment", 1, true)}
                          description={`El cliente accede al ${t("catalog")} pero no puede ordenar`}
                          field="settings.services.dineIn.isEnabled"
                        />
                        <SettingsContainer title="Delivery" description="El cliente recibe su pedido en casa o donde esté" field="delivery">
                          <DeliverySettings />
                        </SettingsContainer>
                        <FlowScheduleSetting
                          title="PickUp"
                          description={`El cliente recoge su pedido ${t("atEstablishment")}`}
                          field="settings.services.pickUp.isEnabled"
                        />
                      </Stack>
                    </Section>

                    <Section title="Checkout" icon={<HugeiconsIcon icon={ShoppingCart02Icon} />}>
                      <SwitchSetting title="Impuesto IVA" description="Habilita/deshabilita el impuesto de IVA" field="settings.includeTax" />
                    </Section>

                    <AppConfig isEnabled={Configs.LOYALTY_ADMIN_CONFIG}>
                      <Section title="Fidelización" icon={<HugeiconsIcon icon={Ticket01Icon} />}>
                        <LoyaltySettingsSection />
                      </Section>
                    </AppConfig>

                    <Section title="Métodos de Pago" icon={<HugeiconsIcon icon={Money03Icon} />}>
                      <>
                        <SwitchSetting title="Efectivo" description="" field="settings.paymentMethods.cash" />
                        <Paper sx={{ p: 2, gap: 2 }}>
                          <Stack spacing={2.5}>
                            <Stack spacing={0.5}>
                              <Typography variant="h4">Tarjetas de crédito / débito</Typography>
                              <Typography variant="body1" color="text.secondary">
                                Selecciona la modalidad de cobro con tarjeta (selección obligatoria y exclusiva)
                              </Typography>
                            </Stack>

                            <ButtonGroup fullWidth variant="outlined">
                              <Button
                                variant={isStripeEnabled ? "contained" : "outlined"}
                                onClick={() => handleSelectPaymentModality("auto")}
                              >
                                Automático
                              </Button>
                              <Button
                                variant={!isStripeEnabled ? "contained" : "outlined"}
                                onClick={() => handleSelectPaymentModality("manual")}
                              >
                                Manual
                              </Button>
                            </ButtonGroup>

                            {isStripeEnabled ? (
                              <Alert severity="info" icon={<HugeiconsIcon icon={ZapIcon} size={32} />}>
                                <AlertTitle>Pasarela de Pago Stripe (Automático)</AlertTitle>
                                Habilita cobros en línea con tarjeta de crédito/débito a través de Stripe.
                              </Alert>
                            ) : (
                              <Stack spacing={2}>
                                <Alert severity="info" icon={<HugeiconsIcon icon={Cashier02Icon} size={32} />}>
                                  <AlertTitle>POS o terminal física (Manual)</AlertTitle>
                                  Envía un POS para cobros físicos con tarjeta.
                                </Alert>
                                <Stack spacing={2} direction={"row"}>
                                  <FormGroup row>
                                    {creditCardOptions.map((option) => (
                                      <FormControlLabel
                                        key={option.value}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              Array.isArray(values?.settings?.paymentMethods?.creditCard)
                                                ? values.settings.paymentMethods.creditCard.includes(option.value as any)
                                                : false
                                            }
                                            onChange={(e) => {
                                              const cards = Array.isArray(values?.settings?.paymentMethods?.creditCard)
                                                ? values.settings.paymentMethods.creditCard
                                                : [];
                                              const updated = e.target.checked
                                                ? [...cards, option.value as any]
                                                : cards.filter((card) => card !== (option.value as any));
                                              manualCreditCardsCacheRef.current = [...updated];
                                              setFieldValue("settings.paymentMethods.creditCard", updated);
                                            }}
                                          />
                                        }
                                        label={option.label}
                                      />
                                    ))}
                                  </FormGroup>
                                </Stack>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                        <Paper sx={{ p: 2, gap: 2 }}>
                          <Stack spacing={2}>
                            <Stack spacing={2} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                              <Typography variant="h4"> Transferencia bancaria</Typography>
                              <Switch
                                checked={bankTransferEnabled}
                                onChange={(e) => {
                                  setFieldValue("settings.paymentMethods.bankTransfer", e.target.checked ? [EMPTY_BANK] : null);
                                }}
                              />
                            </Stack>
                            <Stack spacing={1}>
                              {bankTransferEnabled &&
                                (values.settings?.paymentMethods?.bankTransfer ?? []).map((_, accountIndex) => (
                                  <Stack spacing={2} key={accountIndex} direction={"row"}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Banco</InputLabel>
                                      <Select
                                        label="Banco"
                                        name={`settings.paymentMethods.bankTransfer.${accountIndex}.bankName`}
                                        value={values.settings.paymentMethods.bankTransfer?.[accountIndex]?.bankName ?? ""}
                                        onChange={handleChange}
                                      >
                                        {bankOptions.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>

                                    <FormControl fullWidth size="small">
                                      <InputLabel>Moneda</InputLabel>
                                      <Select
                                        label="Moneda"
                                        name={`settings.paymentMethods.bankTransfer.${accountIndex}.currency`}
                                        value={values.settings.paymentMethods.bankTransfer?.[accountIndex]?.currency ?? ""}
                                        onChange={handleChange}
                                      >
                                        {currencyOptions.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>

                                    <TextField
                                      name={`settings.paymentMethods.bankTransfer.${accountIndex}.accountNumber`}
                                      value={values.settings.paymentMethods.bankTransfer?.[accountIndex]?.accountNumber ?? ""}
                                      size={"small"}
                                      label="No. de Cuenta"
                                      fullWidth
                                      onChange={handleChange}
                                    />
                                    <Tooltip title="Eliminar Cuenta" arrow placement="top">
                                      <IconButton
                                        onClick={() => {
                                          const draft = get(values, "settings.paymentMethods.bankTransfer", []);
                                          draft.splice(accountIndex, 1);
                                          setFieldValue("settings.paymentMethods.bankTransfer", [...draft]);
                                        }}
                                      >
                                        <HugeiconsIcon icon={Delete02Icon} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ))}
                            </Stack>
                            {bankTransferEnabled && (
                              <Stack>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  startIcon={<HugeiconsIcon icon={AddIcon} />}
                                  onClick={() => {
                                    const draft = get(values, "settings.paymentMethods.bankTransfer", []);
                                    setFieldValue("settings.paymentMethods.bankTransfer", [...draft, EMPTY_BANK]);
                                  }}
                                >
                                  Agregar Cuenta
                                </Button>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      </>
                    </Section>

                    <Section title="Personalización" icon={<HugeiconsIcon icon={PaintBrush02Icon} />}>
                      <Stack gap={2}>
                        <ColorSetting field="primaryColor" title="Color primario" description="Color principal, se utiliza en botones y títulos" />
                        <ColorSetting field="secondaryColor" title="Color secundario" description="Se utiliza como complemento, en fondos y botones secundarios" />
                      </Stack>
                    </Section>

                    <Section title="Lobby" icon={<HugeiconsIcon icon={Home06Icon} />}>
                      <Stack gap={2}>
                        <FileSetting field="header" title="Banner Principal" description="Imagen utilizada de banner junto al logotipo" />
                        <FileSetting field="logo" title="Logotipo" description="Combinación de palabra/letras y símbolo que representen la marca" />
                        <FileSetting field="isotype" title="Isotipo" description="Símbolo que representen la marca" />
                      </Stack>
                    </Section>

                    <Section title="Pedidos" icon={<HugeiconsIcon icon={FolderLibraryIcon} />}>
                      <Stack gap={2}>
                        <SelectSetting
                          field={"settings.orderManagement"}
                          title={"Administración"}
                          description={"Selecciona el método para recibir y gestionar tus pedidos"}
                          options={[
                            { label: "WhatsApp", value: "whatsapp" },
                            { label: "BiteLink", value: "system" },
                          ]}
                        />
                      </Stack>
                    </Section>

                    <Stack spacing={3} direction="row" justifyContent="center">
                      {hasAccess("delete.organization") ? (
                        <LoadingButton
                          variant="contained"
                          loading={isLoading}
                          color="error"
                          onClick={() => {
                            if (window.confirm("Seguro deseas eliminar esta organización?")) {
                              handleDeleteOrg();
                            }
                          }}
                        >
                          Eliminar Comercio
                        </LoadingButton>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default withAccessControl(Settings, "update.organization");
